import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Archive, Download, Loader2, Upload, FolderDown, ScrollText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { downloadDocument } from "@/lib/documentDownload";
import { buildVaultZip, triggerBlobDownload } from "@/lib/vaultExport";
import { EvidenceUploadDialog } from "@/components/governance/EvidenceUploadDialog";
import { formatDateString } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { resolveAdminUna } from "@/lib/succession/stages";

type RecordDoc = {
  id: string;
  document_name: string;
  file_path: string | null;
  file_url: string | null;
  file_size: number | null;
  generated_at: string | null;
  uploaded_filename: string | null;
};

function formatSize(bytes: number | null): string {
  if (!bytes) return "size not recorded";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface Props {
  unaId: string | undefined;
  unaName?: string | null;
}

/**
 * The Record — member filing cabinet. Uses the vault storage lane under the hood.
 * User-facing copy is "The Record"; code identifiers keep "vault" for continuity.
 */
export function TheRecordContent({ unaId, unaName }: Props) {
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ current: number; total: number } | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: isAdmin = false } = useQuery({
    queryKey: ["record-succession-admin", user?.id, unaId],
    queryFn: async () => {
      if (!user?.id || !unaId) return false;
      const una = await resolveAdminUna(user.id);
      return !!una && una.id === unaId;
    },
    enabled: !!user?.id && !!unaId,
  });

  const { data: successionDocs = [] } = useQuery({
    queryKey: ["record-succession-docs", unaId, isAdmin],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("succession_documents")
        .select("id, doc_type, title, filed_at")
        .eq("association_id", unaId!)
        .eq("status", "signed_filed")
        .order("filed_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!unaId && isAdmin,
  });

  const { data: docs = [], isLoading, isError } = useQuery({
    queryKey: ["vault-docs", unaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generated_documents")
        .select("id, document_name, file_path, file_url, file_size, generated_at, uploaded_filename")
        .eq("una_id", unaId!)
        .eq("document_type", "vault")
        .order("generated_at", { ascending: false });
      if (error) throw error;
      return (data || []) as RecordDoc[];
    },
    enabled: !!unaId,
  });

  const handleDownload = async (doc: RecordDoc) => {
    setDownloadingId(doc.id);
    try {
      await downloadDocument(doc);
    } catch (e: any) {
      toast.error(e?.message || "That download did not go through. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleExportAll = async () => {
    if (!unaId || exporting) return;
    setExporting(true);
    setExportProgress({ current: 0, total: 0 });
    try {
      const result = await buildVaultZip(unaId, unaName || "Association", (p) => {
        setExportProgress({ current: p.current, total: p.total });
      });
      if (result.included === 0) {
        toast.error("There is nothing to export yet.");
        return;
      }
      triggerBlobDownload(result.blob, result.filename);
      if (result.skipped.length > 0) {
        toast.success(`Exported ${result.included} of ${result.included + result.skipped.length}.`, {
          description: `Skipped (no file available): ${result.skipped.slice(0, 6).join(", ")}${result.skipped.length > 6 ? `, and ${result.skipped.length - 6} more` : ""}.`,
        });
      } else {
        toast.success(`Exported ${result.included} documents.`);
      }
    } catch (e: any) {
      toast.error(e?.message || "That export did not go through. Please try again.");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  if (!unaId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>The Record</CardTitle>
          <CardDescription>Your Record will appear here once your UNA is set up.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-gold/30">
        <CardHeader className="flex flex-col sm:flex-row items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-gold" />
              <CardTitle className="text-2xl">The Record</CardTitle>
            </div>
            <CardDescription className="mt-2">
              A safe place to keep copies of your Constitution and core packet. Store what matters, so a second copy
              always exists.
            </CardDescription>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={handleExportAll} disabled={exporting || !unaId}>
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderDown className="w-4 h-4 mr-2" />}
              {exporting && exportProgress && exportProgress.total > 0
                ? `Export All (${exportProgress.current} of ${exportProgress.total})`
                : "Export All"}
            </Button>
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Add to The Record
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Stored documents</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center text-muted-foreground py-8">Loading your documents.</div>
          ) : isError ? (
            <div className="text-center text-muted-foreground py-8">
              We could not load your Record just now. Please refresh in a moment.
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-10 space-y-3">
              <Archive className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Your Record is empty. Start with your Constitution and the documents you would want a trusted successor
                to be able to find.
              </p>
              <Button variant="outline" onClick={() => setUploadOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Add your first document
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {docs.map((doc) => (
                <div key={doc.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{doc.document_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {doc.generated_at ? formatDateString(doc.generated_at) : "date not recorded"}
                      {" · "}
                      {formatSize(doc.file_size)}
                      {doc.uploaded_filename ? ` · ${doc.uploaded_filename}` : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleDownload(doc)} disabled={downloadingId === doc.id}>
                    {downloadingId === doc.id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Download className="w-3 h-3 mr-1" />
                    )}
                    Download
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-gold" />
              <CardTitle className="text-lg">Succession</CardTitle>
            </div>
            <CardDescription>Your filed succession papers. Read only here. Edit from your Succession pages.</CardDescription>
          </CardHeader>
          <CardContent>
            {successionDocs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                When you sign and file your succession papers, they will show up here.
              </p>
            ) : (
              <div className="divide-y">
                {successionDocs.map((d: any) => (
                  <div key={d.id} className="py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{d.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Filed on {d.filed_at ? formatDateString(d.filed_at) : "date not recorded"}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/succession/documents#${d.doc_type}`)}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Open
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}


      <EvidenceUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        unaId={unaId}
        mode="vault"
        onUploaded={() => queryClient.invalidateQueries({ queryKey: ["vault-docs", unaId] })}
      />
    </div>
  );
}
